<script setup lang="ts">
import logoUrl from '../../../assets/images/ai-landing/clovyr-icon-1.svg';

const links = [
  {
    heading: 'Company',
    items: [
      { label: 'About Clovyr', href: 'https://clovyr.io' },
      { label: 'Blog', href: 'https://blog.clovyr.io/' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: 'https://clovyr.io/terms-of-service' },
      { label: 'Documentation', href: 'https://help.clovyr.io/' },
    ],
  },
];
</script>

<template>
  <footer class="ai-footer">
    <div class="ai-footer__inner">
      <div class="ai-footer__grid">
        <div class="ai-footer__brand-col">
          <div class="ai-footer__brand">
            <img :src="logoUrl" alt="Clovyr logo" />
            <span>Clovyr <strong>AI Cloud</strong></span>
          </div>

          <p class="ai-footer__summary">The private AI workspace for teams who own their data.</p>
        </div>

        <div v-for="column in links" :key="column.heading" class="ai-footer__col">
          <div class="ai-footer__heading">{{ column.heading }}</div>
          <div class="ai-footer__links">
            <a
              v-for="item in column.items"
              :key="item.label"
              :href="item.href"
              :target="item.href.startsWith('http') ? '_blank' : undefined"
              rel="noopener noreferrer"
            >
              {{ item.label }}
            </a>
          </div>
        </div>
      </div>

      <div class="ai-footer__bottom">
        <div class="ai-footer__copy">
          © {{ new Date().getFullYear() }} Clovyr Inc. All rights reserved.
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.ai-footer {
  background: #1a1228;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.ai-footer__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 3.5rem 2rem;
}

.ai-footer__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.25rem;
  margin-bottom: 2.5rem;
}

.ai-footer__brand-col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  grid-template-columns: 1fr;
}

.ai-footer__brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ai-footer__brand img {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
}

.ai-footer__brand span {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.ai-footer__brand strong {
  color: #00bf74;
  font-weight: 700;
}

.ai-footer__summary {
  margin: 0;
  max-width: 220px;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.625;
}

.ai-footer__heading {
  color: #9ca3af;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.75rem;
}

.ai-footer__links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ai-footer__links a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 200ms ease;
}

.ai-footer__links a:hover {
  color: #d1d5db;
}

.ai-footer__bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.ai-footer__copy {
  color: #374151;
  font-size: 0.75rem;
}

@media (min-width: 1024px) {
  .ai-footer__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: start;
  }

  .ai-footer__bottom {
    flex-direction: row;
  }
}

@media (max-width: 800px) {
  .ai-footer__inner {
    padding: 3rem 1.5rem 2rem;
  }
}

@media (max-width: 640px) {
  .ai-footer__inner {
    padding: 2.5rem 1rem 1.75rem;
  }

  .ai-footer__grid {
    gap: 1.75rem;
    margin-bottom: 1.75rem;
  }

  .ai-footer__brand img {
    width: 1.45rem;
    height: 1.45rem;
  }

  .ai-footer__brand span {
    font-size: 0.92rem;
  }

  .ai-footer__summary,
  .ai-footer__links a {
    font-size: 0.82rem;
  }
}
</style>
